import { IfWrites } from '../../components/write-access';
import { Fragment, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type {
  OrganisationProfile,
  OrganisationRecord,
  OrgResearchDepth,
  RunDetail,
} from '@ni/shared';
import { useOrg } from '../../api/app-context';
import { useKnowledgePanel, useOrganisationRunStateInvalidation, useRun } from '../../api/hooks';
import {
  ORGANISATION_ASIDE,
  ORGANISATION_NO_SUMMARY,
  ORGANISATION_PROFILE_HEAD,
  ORGANISATION_PROFILE_LABELS,
  ORGANISATION_SUMMARY_HEAD,
  RESEARCH_ASIDE,
  RESEARCH_STATE_WORDS,
  organisationAlsoSeenAs,
  organisationAlsoWebsites,
  ORG_LOOKUP_ACTIONS,
  organisationProfileSourceLine,
  organisationRunFailedLine,
  organisationLeaderLine,
  organisationSummaryUpdated,
  organisationWhereLine,
  RUN_UNDELIVERED_HEAD,
  undeliveredLine,
} from '../../lib/copy';
import { formatDay } from '../../lib/format';
import {
  latestRunId,
  organisationEverInvestigated,
  organisationEverLookedUp,
  organisationRunActive,
  organisationRunHolds,
  isOfficeEndedRun,
} from '../../lib/run-state';
import { OrganisationLookupModal } from '../../components/organisations/lookup-modal';
import { leaderCards, type LeaderCard } from '../../components/organisations/people-groups';
import { Evidence, PanelHead } from '../../components/brief/fact-sections';
import { useSourceIndex } from '../../components/brief/fact-sources';
import { PeopleOnRecord, useRecordTies } from '../../components/brief/research-aside';
import { panelFactLine } from '../../components/knowledge/knowledge-panel';
import { Prose } from '../../components/ui/prose';
import { SiteLink } from '../../components/ui/site-link';
import { RunBlock } from '../../components/run-strip/run-block';
import { orgLookupDepth } from '../../components/run-strip/run-strip';
import { useOrganisationRecord } from './record';
import { EmptyLine } from '../../components/ui/empty-line';
import { Button } from '../../components/ui/button';
import '../../components/record/record-body.css';

/**
 * **The organisation's Overview tab** (blend spec, the record body): the
 * summary first with the facts behind it in one closed disclosure, and beside
 * it the record's properties — About, where research stands, leadership,
 * connections and the people around it already on record.
 *
 * Above the summary is one slot with one occupant at a time (owner rulings,
 * 2026-09-09 and 2026-09-10): the run block while a run is going; otherwise the
 * button that asks the next question, Look this up and then Investigate, and
 * nothing once an investigation has landed, Refresh then being the foot's.
 * Both buttons open the lookup box first; neither starts anything on the press.
 */

/**
 * **The rows of the About block**, in the order a reader asks them in, and only
 * the ones the source stated: a row whose value came back null is not a row.
 */
export function profileRows(
  profile: OrganisationProfile,
): { label: string; value: string; site?: true }[] {
  const rows: { label: string; value: string | null; site?: true }[] = [
    {
      label: ORGANISATION_PROFILE_LABELS.where,
      value: organisationWhereLine(profile.location, profile.jurisdiction),
    },
    { label: ORGANISATION_PROFILE_LABELS.founded, value: profile.founded },
    { label: ORGANISATION_PROFILE_LABELS.size, value: profile.size },
    { label: ORGANISATION_PROFILE_LABELS.sector, value: profile.sector },
    /* The one row that is an address rather than a value, and the flag is how the
       renderer knows without matching on the label. */
    { label: ORGANISATION_PROFILE_LABELS.website, value: profile.website, site: true },
  ];
  return rows.flatMap((row) => {
    const value = row.value?.trim();
    return value ? [{ ...row, value }] : [];
  });
}

/**
 * **About — what a source said this organisation is**, at the head of the
 * aside: the description, then the stated values, then where it was read.
 * Nothing at all when there is no profile; the summary's own "No summary yet."
 * already says nothing has read the record.
 *
 * The website is a link (owner ruling, 2026-09-10), and the record's other
 * sites sit under it as text (owner ruling R10): "Also …" for addresses the
 * record answers at, "Also seen as …" for hosts a page merely printed.
 */
export function OrganisationProfileBlock({
  profile,
  websites = [],
  aliases = [],
}: {
  profile: OrganisationProfile | null;
  /** Every website the record answers at, primary first — the head's, not the profile's. */
  websites?: readonly string[];
  /** The hosts pages merely printed. */
  aliases?: readonly string[];
}) {
  if (!profile) return null;

  const rows = profileRows(profile);
  const sources = organisationProfileSourceLine(profile.sources);
  const also = organisationAlsoWebsites(otherWebsites(profile.website, websites));
  const seenAs = organisationAlsoSeenAs(otherWebsites(profile.website, aliases));

  return (
    <div className="props-block">
      <h4 className="sc">{ORGANISATION_PROFILE_HEAD}</h4>
      <div className="record-about">
        <Prose text={profile.description} />
      </div>
      {rows.length > 0 ? (
        <dl className="prop-list">
          {rows.map((row) => (
            <Fragment key={row.label}>
              <dt>{row.label}</dt>
              <dd>
                {row.site ? <SiteLink value={row.value} /> : row.value}
                {row.site && also ? <span className="quiet">{also}</span> : null}
                {row.site && seenAs ? <span className="quiet">{seenAs}</span> : null}
              </dd>
            </Fragment>
          ))}
        </dl>
      ) : null}
      {sources ? <p className="record-from">{sources}</p> : null}
    </div>
  );
}

/**
 * **The hosts on a list that are not the one on the row** (owner ruling,
 * 2026-09-10), in the record's own order. The comparison is on the trimmed
 * string: both sides come off the same projection, so the row's value is a
 * member of this list spelled exactly as the list spells it.
 */
export function otherWebsites(shown: string | null, websites: readonly string[]): string[] {
  const on = shown?.trim() ?? '';
  return websites.map((host) => host.trim()).filter((host) => host.length > 0 && host !== on);
}

/**
 * **Why the last run failed**, or null where the question does not arise. The
 * run block leaves the page when the run ends, so the reason stands under the
 * button that asks again. A run the office ended itself says nothing: its reason
 * is the answer the office just gave.
 */
export function organisationRunFailure(
  runs: OrganisationRecord['runs'],
  polled: RunDetail | null,
): string | null {
  const latest = runs[0] ?? null;
  const run = polled && (!latest || polled.id === latest.id) ? polled : latest;
  if (!run || run.state !== 'failed' || isOfficeEndedRun(run)) return null;
  /* The summary carries the depth the office pressed; the polled detail has only
     its plan, which `orgLookupDepth` reads the way the run block does. */
  const depth =
    latest && latest.id === run.id && latest.lookup_depth
      ? latest.lookup_depth
      : polled && polled.id === run.id
        ? orgLookupDepth(polled)
        : 'lookup';
  return organisationRunFailedLine(depth, run.error?.message ?? null);
}

/**
 * The aside's research line: "Running" while the run block beside it is up,
 * otherwise the deepest reading that finished and when the latest of that depth
 * did. "Investigated" outranks "Looked up" for the reason the buttons do: a
 * record read that deeply has been read that deeply.
 */
export function organisationResearchLine(
  runs: OrganisationRecord['runs'],
  runActive = false,
): {
  word: string;
  day: string | null;
} {
  if (runActive) return { word: RESEARCH_STATE_WORDS.running, day: null };
  const finished = runs.filter((run) => run.kind === 'org_lookup' && run.state === 'complete');
  // A lookup that found nothing was not charged; it reads as the credits page reads it.
  const done = finished.filter((run) => run.delivered !== false);
  const investigated = done.filter((run) => run.lookup_depth === 'investigation');
  const latest = investigated[0] ?? done[0];
  if (!latest) {
    const empty = finished[0];
    return empty
      ? {
          word: `${RUN_UNDELIVERED_HEAD} · ${undeliveredLine(empty.kind)}`,
          day: formatDay(empty.completed_at ?? empty.created_at),
        }
      : { word: RESEARCH_STATE_WORDS.none, day: null };
  }
  return {
    word: investigated.length > 0 ? ORGANISATION_ASIDE.investigated : ORGANISATION_ASIDE.lookedUp,
    day: formatDay(latest.completed_at ?? latest.created_at),
  };
}

/** One leader in the aside: the name, a link where the office keeps their record. */
function AsideLeader({ leader }: { leader: LeaderCard }) {
  const under = leader.roles.map(organisationLeaderLine).join('; ');
  return (
    <span className="record-lead">
      {leader.tracked ? (
        <Link className="person" to={`/people/${leader.person_entity_id}`}>
          {leader.display_name}
        </Link>
      ) : (
        <span className="person">{leader.display_name}</span>
      )}
      {under ? <span className="quiet">{under}</span> : null}
    </span>
  );
}

/** Leaders the aside names before pointing at the People tab for the rest. */
const ASIDE_LEADERS = 2;

/**
 * **The record's properties, beside the summary**: About, then where research
 * stands, who leads it and how connected it is, then the people around it
 * already on record. Hook-free, so it is asserted as static markup; the ties
 * arrive from the Network read, null until it lands.
 */
export function OrganisationAside({
  record,
  vertical,
  ties,
  runActive = false,
}: {
  record: OrganisationRecord;
  vertical: string;
  ties: ReturnType<typeof useRecordTies>;
  /** A run is going: the research line says so rather than the last result. */
  runActive?: boolean;
}) {
  const { subject, leadership } = record;
  const research = organisationResearchLine(record.runs, runActive);
  const leaders = leaderCards(leadership);
  const shownLeaders = leaders.slice(0, ASIDE_LEADERS);
  const moreLeaders = leaders.length - shownLeaders.length;

  return (
    <aside className="record-aside">
      <OrganisationProfileBlock
        profile={record.profile}
        websites={subject.websites}
        aliases={subject.aliases}
      />
      <div className="props-block">
        <dl className="prop-list">
          <dt>{RESEARCH_ASIDE.research}</dt>
          <dd>
            {research.word}
            {research.day ? <span className="quiet num">{research.day}</span> : null}
          </dd>
          {shownLeaders.length > 0 ? (
            <>
              <dt>{ORGANISATION_ASIDE.leadership}</dt>
              <dd>
                {shownLeaders.map((leader) => (
                  <AsideLeader key={leader.person_entity_id} leader={leader} />
                ))}
                {moreLeaders > 0 ? (
                  <Link className="record-more" to={`/organizations/${subject.entity_id}/people`}>
                    {ORGANISATION_ASIDE.moreLeaders(moreLeaders)}
                  </Link>
                ) : null}
              </dd>
            </>
          ) : null}
          <dt>{RESEARCH_ASIDE.connections}</dt>
          <dd className="num">{subject.connections}</dd>
        </dl>
      </div>
      {ties ? <PeopleOnRecord vertical={vertical} people={ties.onRecord} /> : null}
    </aside>
  );
}

/**
 * **The tab, given the record**: nothing here fetches, so it is asserted as
 * static markup. The run block, the facts disclosure and the aside's ties are
 * read by the writer below and handed in.
 */
export function OrganisationOverviewView({
  record,
  runStrip,
  runActive,
  moving = runActive,
  everLookedUp,
  everInvestigated,
  runFailure,
  onAsk,
  evidence = null,
  vertical = '',
  ties = null,
}: {
  record: OrganisationRecord;
  /** The run block, or null where there is no run — the writer decides. */
  runStrip: ReactNode;
  /** The run block is in the slot, not a button (`organisationRunHolds`). */
  runActive: boolean;
  /** The pipeline is working on the record (`organisationRunActive`); the aside says so. */
  moving?: boolean;
  /** A lookup has landed here, so what the record is offered next is Investigate. */
  everLookedUp: boolean;
  /** An investigation has landed here, so the slot offers nothing; Refresh is the foot's. */
  everInvestigated: boolean;
  /** Why the last run failed, said in the run block's absence. */
  runFailure: string | null;
  /** Both buttons open the box, at the depth they name. */
  onAsk: (depth: OrgResearchDepth) => void;
  /** "Show the N facts behind this", once the record's facts are read. */
  evidence?: ReactNode;
  vertical?: string;
  ties?: ReturnType<typeof useRecordTies>;
}) {
  const { summary } = record;

  return (
    <div className="record-body">
      <div className="record-main">
        {runActive ? (
          <IfWrites>{runStrip}</IfWrites>
        ) : (
          <>
            {everInvestigated ? null : (
              <IfWrites>
                <div className="form-actions lead">
                  <Button
                    variant="go"
                    onClick={() => onAsk(everLookedUp ? 'investigation' : 'lookup')}
                  >
                    {everLookedUp ? ORG_LOOKUP_ACTIONS.investigation : ORG_LOOKUP_ACTIONS.lookup}
                  </Button>
                </div>
              </IfWrites>
            )}
            {/* On its own row under the button (owner ruling, 2026-09-10): the
                engine's reasons are prose and wrap like it. */}
            {runFailure ? <div className="src run-failed">{runFailure}</div> : null}
          </>
        )}

        <section className="r-panel">
          <PanelHead
            title={ORGANISATION_SUMMARY_HEAD}
            meta={summary ? organisationSummaryUpdated(formatDay(summary.updated_at)) : null}
          />
          {summary ? (
            <div className="reading">
              <Prose text={summary.text} />
            </div>
          ) : (
            <EmptyLine>{ORGANISATION_NO_SUMMARY}</EmptyLine>
          )}
          {evidence}
        </section>
      </div>

      <OrganisationAside record={record} vertical={vertical} ties={ties} runActive={moving} />
    </div>
  );
}

/**
 * The record's facts behind one closed disclosure, read-only: the knowledge
 * panel's read of this organisation, each sentence written for it
 * (`panelFactLine`), with the Data read's sources in the margin.
 */
function OrganisationEvidence({
  entityId,
  name,
  poll,
}: {
  entityId: string;
  name: string;
  /** Re-read on the minute only while a run is going (`useNetwork`'s `poll`). */
  poll: boolean;
}) {
  const panel = useKnowledgePanel(entityId, undefined, { poll });
  const facts = panel.data?.facts ?? [];
  const index = useSourceIndex(entityId, facts.length > 0);
  if (!panel.data || facts.length === 0) return null;
  const entry = panel.data.entry;
  return (
    <Evidence
      subjectId={entityId}
      subjectName={name}
      facts={facts}
      index={index}
      line={(fact) => panelFactLine(fact, entry)}
      readOnly
    />
  );
}

/**
 * The Overview tab as the route mounts it: it owns the run it narrates and the
 * box both buttons open. The box creates the run, at the depth the press asked.
 */
export function OrganisationOverviewTab() {
  const { entityId, record } = useOrganisationRecord();
  const vertical = useOrg().vertical;

  /** The depth the box is open at, or null where it is shut. */
  const [asking, setAsking] = useState<OrgResearchDepth | null>(null);

  /* The run the block narrates: the record's newest, or the id the create just
     handed back until the record has been read since. The started id wins, or a
     second lookup would show the previous, completed run over one that is going. */
  const [startedRunId, setStartedRunId] = useState<string | null>(null);
  const runs = record.runs;
  const started = runs.some((run) => run.id === startedRunId) ? null : startedRunId;
  const runQuery = useRun(started ?? latestRunId(runs));
  useOrganisationRunStateInvalidation(entityId, runQuery.data);

  const runActive = organisationRunActive(runs, runQuery.data?.state ?? null, started);
  const held = organisationRunHolds(runs, runQuery.data ?? null, started);
  const ties = useRecordTies(entityId, { poll: runActive });

  return (
    <>
      <OrganisationOverviewView
        record={record}
        runStrip={
          runQuery.data ? (
            <RunBlock
              subjectId={entityId}
              subjectName={record.subject.display_name}
              run={runQuery.data}
              pendingDecisions={record.pending_decisions}
              pollFailures={runQuery.failureCount}
            />
          ) : null
        }
        runActive={held}
        moving={runActive}
        everLookedUp={organisationEverLookedUp(runs)}
        everInvestigated={organisationEverInvestigated(runs)}
        runFailure={organisationRunFailure(runs, runQuery.data ?? null)}
        onAsk={(depth) => setAsking(depth)}
        evidence={
          <OrganisationEvidence
            entityId={entityId}
            name={record.subject.display_name}
            poll={runActive}
          />
        }
        vertical={vertical}
        ties={ties}
      />

      {/* The box files what the office typed and then starts the run, in that
          order, so the words are inside the context the run freezes. */}
      {asking ? (
        <OrganisationLookupModal
          entityId={entityId}
          mode={asking}
          onClose={() => setAsking(null)}
          onStarted={(runId) => setStartedRunId(runId)}
        />
      ) : null}
    </>
  );
}
